import { PropsWithChildren, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LitNavigationDrawerModal } from "./LitNavigationDrawerModal";
import { sysColors } from "@/assets/theme/colors";
import { useIsMobile } from "@/hooks/useIsMobile";
import { isOverlayEscapeKeyActive, useOverlayDismiss } from "@/hooks/useOverlayDismiss";

const showDevBanner = import.meta.env.MODE === "dev";

interface Props {
  isOpen: boolean;
  onChange(isOpen: boolean): void;
  "data-testid"?: string;
  zIndex: number;
  disableClose?: boolean;
  dismissOnOutsideClick?: boolean;
  persistentTabLabel?: string;
  persistentTabStatus?: "active" | "inactive";
}

type DrawerChangeEvent = CustomEvent<{ opened?: boolean }> & {
  currentTarget: HTMLElement & { opened: boolean };
};

function getScrollDirection(setIsScrolled: (isScrolled: boolean) => void) {
  const threshold = 10;
  let ticking = false;

  const updateScrollState = () => {
    setIsScrolled(window.scrollY > threshold);
    ticking = false;
  };

  const onScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(updateScrollState);
      ticking = true;
    }
  };

  window.addEventListener("scroll", onScroll);

  return () => window.removeEventListener("scroll", onScroll);
}

export default function DrawerModal({
  zIndex,
  isOpen,
  "data-testid": testid,
  onChange,
  disableClose = false,
  dismissOnOutsideClick = true,
  persistentTabLabel,
  persistentTabStatus = "active",
  children
}: PropsWithChildren<Props>) {
  const isMobile = useIsMobile();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const drawerWidth = isMobile ? "100%" : "520px";
  const baseOffset = showDevBanner ? 13 : 9.5;
  const scrollAdjustment = isScrolled ? (showDevBanner ? 6 : 2) : 0;
  const topOffset = `${baseOffset - scrollAdjustment}rem`;
  const navStartOffset = "2.25rem";
  const tabColorClassName =
    persistentTabStatus === "active"
      ? "bg-extended-success-brand-color text-extended-success-brand-on-color"
      : "bg-extended-success-brand-color-container text-extended-success-brand-on-color-container";
  const statusDotClassName =
    persistentTabStatus === "active"
      ? "bg-extended-success-brand-on-color animate-pulse"
      : "bg-extended-success-brand-on-color-container opacity-50";

  const customStyles = {
    "--md-navigation-drawer-modal-scrim-color": sysColors["sys-brand-scrim"],
    "--md-navigation-drawer-modal-scrim-opacity": 0.32,
    "--md-navigation-drawer-modal-container-shape": "0",
    "--md-navigation-drawer-modal-container-width": drawerWidth
  } as React.CSSProperties;

  useEffect(() => {
    if (!isOpen) {
      setDrawerOpened(false);
      return;
    }

    setDrawerOpened(false);
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setDrawerOpened(true));
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const scrollY = window.scrollY;
    document.body.style.overflow = "hidden";

    if (isMobile) {
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";

      const preventTouchMove = (e: TouchEvent) => {
        const path = e.composedPath();
        for (const node of path) {
          if (!(node instanceof HTMLElement)) continue;
          if (node === document.body || node === document.documentElement) break;

          const style = getComputedStyle(node);
          const isScrollableY = style.overflowY === "auto" || style.overflowY === "scroll";
          const isScrollableX = style.overflowX === "auto" || style.overflowX === "scroll";

          if (
            (isScrollableY && node.scrollHeight > node.clientHeight) ||
            (isScrollableX && node.scrollWidth > node.clientWidth)
          ) {
            return;
          }
        }
        e.preventDefault();
      };

      document.body.addEventListener("touchmove", preventTouchMove, { passive: false });

      return () => {
        document.body.removeEventListener("touchmove", preventTouchMove);
        const savedScrollY = document.body.style.top;
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.overflow = "visible";
        if (savedScrollY) {
          window.scrollTo(0, parseInt(savedScrollY || "0") * -1);
        }
      };
    }

    return () => {
      document.body.style.overflow = "visible";
    };
  }, [isOpen, isMobile]);

  useEffect(() => {
    const cleanup = getScrollDirection(setIsScrolled);
    return cleanup;
  }, []);

  const allowOutsideDismiss = isOpen && !disableClose && dismissOnOutsideClick;

  useOverlayDismiss({
    open: isOpen,
    disableClose,
    dismiss: () => onChange(false)
  });

  const handlePersistentTabClick = () => {
    if (disableClose && isOpen) return;
    onChange(!isOpen);
  };

  const handleDrawerChange = (event: Event) => {
    const drawerEvent = event as DrawerChangeEvent;
    const nextOpen = drawerEvent.detail?.opened;
    if (typeof nextOpen !== "boolean" || nextOpen === isOpen) return;
    // Escape: keep Material opened and let the overlay stack dismiss once (mirrors Dialog).
    if (!nextOpen && (disableClose || isOverlayEscapeKeyActive())) {
      drawerEvent.currentTarget.opened = true;
      return;
    }
    onChange(nextOpen);
  };

  const getDrawerElement = () =>
    wrapperRef.current?.querySelector("md-navigation-drawer-modal") as HTMLElement | undefined;

  useEffect(() => {
    if (!isOpen) return;
    const drawer = getDrawerElement();
    if (!drawer) return;

    const restoreOpened = (event: KeyboardEvent) => {
      if (event.key !== "Escape" && event.code !== "Escape") return;
      (drawer as HTMLElement & { opened: boolean }).opened = true;
    };

    drawer.addEventListener("keydown", restoreOpened);
    return () => drawer.removeEventListener("keydown", restoreOpened);
  }, [isOpen, drawerOpened]);

  const getDrawerParts = () => {
    const shadowRoot = getDrawerElement()?.shadowRoot;
    return {
      drawerElement: shadowRoot?.querySelector(".md3-navigation-drawer-modal") as HTMLElement,
      scrim: shadowRoot?.querySelector(".md3-navigation-drawer-modal__scrim") as HTMLElement,
      slot: shadowRoot?.querySelector("slot")
    };
  };

  const positionDrawerFromEnd = useCallback(() => {
    const navigationDrawer = getDrawerElement();
    if (navigationDrawer?.getAttribute("side") !== "end") {
      navigationDrawer?.setAttribute("side", "end");
    }

    const { drawerElement, scrim, slot } = getDrawerParts();
    if (scrim) Object.assign(scrim.style, { zIndex, position: "fixed" });

    if (drawerElement) {
      Object.assign(drawerElement.style, {
        zIndex: zIndex + 1,
        right: "0",
        left: "auto",
        position: "fixed"
      });
    }

    if (slot) {
      Object.assign(slot.style, {
        display: "block",
        position: "relative",
        height: "100%",
        width: "100%"
      });
    }
  }, [zIndex]);

  useLayoutEffect(() => {
    positionDrawerFromEnd();

    if (!drawerOpened) return;

    const timeout1 = window.setTimeout(positionDrawerFromEnd, 10);
    const timeout2 = window.setTimeout(positionDrawerFromEnd, 50);
    const raf1 = requestAnimationFrame(positionDrawerFromEnd);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(positionDrawerFromEnd));

    return () => {
      window.clearTimeout(timeout1);
      window.clearTimeout(timeout2);
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [drawerOpened, drawerWidth, positionDrawerFromEnd]);

  useLayoutEffect(() => {
    const { scrim } = getDrawerParts();
    if (allowOutsideDismiss || !scrim) return;

    const preventScrimClose = (event: Event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    scrim.addEventListener("pointerdown", preventScrimClose, true);
    scrim.addEventListener("mousedown", preventScrimClose, true);
    scrim.addEventListener("click", preventScrimClose, true);

    return () => {
      scrim.removeEventListener("pointerdown", preventScrimClose, true);
      scrim.removeEventListener("mousedown", preventScrimClose, true);
      scrim.removeEventListener("click", preventScrimClose, true);
    };
  }, [allowOutsideDismiss, drawerOpened]);

  const modalContent = (
    <div ref={wrapperRef} style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex }}>
      {allowOutsideDismiss && (
        <div
          data-testid="overlay-dismiss-scrim"
          aria-hidden="true"
          onClick={() => onChange(false)}
          style={{ position: "absolute", inset: 0, pointerEvents: "auto" }}
        />
      )}
      <LitNavigationDrawerModal
        opened={drawerOpened}
        onChange={handleDrawerChange}
        style={{ ...customStyles, pointerEvents: "auto", position: "relative", zIndex: 1 } as React.CSSProperties}
        data-testid={testid}
      >
        {children}
      </LitNavigationDrawerModal>
      {persistentTabLabel && !(isMobile && isOpen) && (
        <button
          type="button"
          className={`absolute rounded-l-lg px-2 py-3 text-label-large font-semibold shadow-lg transition-[right] duration-200 ease-out focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-extended-success-brand-color pointer-events-auto ${tabColorClassName}`}
          style={{
            top: `calc(${topOffset} + ${navStartOffset})`,
            right: isOpen && !isMobile ? drawerWidth : 0,
            zIndex: zIndex + 2
          }}
          aria-expanded={isOpen}
          aria-label={isOpen ? `Collapse ${persistentTabLabel}` : `Open ${persistentTabLabel}`}
          onClick={handlePersistentTabClick}
        >
          <span className="flex flex-col items-center gap-2 p-1">
            <span
              className="block"
              style={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)"
              }}
            >
              {persistentTabLabel}
            </span>
            <span
              aria-hidden="true"
              className={`block shrink-0 rounded-full ${statusDotClassName}`}
              style={{ width: 8, height: 8, minWidth: 8, minHeight: 8 }}
            />
          </span>
        </button>
      )}
    </div>
  );

  // Render the modal using a portal to ensure it's at the document root level
  // This prevents stacking context issues with parent elements
  return createPortal(modalContent, document.body);
}
